/**
 * Утилиты для работы с Android WebView
 */

/**
 * Определяет, запущено ли приложение в Android WebView
 */
export function isAndroidWebView() {
    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('wv') || // Android WebView
           (userAgent.includes('android') && userAgent.includes('chrome') && !userAgent.includes('mobile'));
}

/**
 * Определяет версию Android
 */
export function getAndroidVersion() {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(/Android\s([0-9.]*)/);
    return match ? parseFloat(match[1]) : null;
}

/**
 * Проверяет поддержку множественного выбора файлов
 */
export function supportsMultipleFileSelection() {
    // Проверяем поддержку File API
    if (!window.File || !window.FileList || !window.FileReader) {
        return false;
    }
    
    // Создаем тестовый input
    const testInput = document.createElement('input');
    testInput.type = 'file';
    testInput.multiple = true;
    
    // Проверяем, поддерживается ли атрибут multiple
    return testInput.multiple === true;
}

/**
 * Создает кастомный селектор файлов для Android WebView
 */
export function createCustomFileSelector(options = {}) {
    const {
        multiple = true,
        accept = 'image/*, video/*',
        maxCount = 10,
        onFilesSelected = () => {},
        onError = () => {}
    } = options;

    // Создаем скрытый input для каждого файла
    const createSingleFileInput = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.style.display = 'none';
        input.multiple = false; // Всегда false для Android WebView
        
        return input;
    };

    // Создаем контейнер для кнопок
    const container = document.createElement('div');
    container.className = 'android-file-selector';
    container.style.cssText = `
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 15px;
        border: 2px dashed #ccc;
        border-radius: 8px;
        background: #f9f9f9;
    `;

    const selectedFiles = [];
    let currentFileIndex = 0;

    // Функция для добавления нового файла
    const addFile = () => {
        if (currentFileIndex >= maxCount) {
            onError(new Error(`Максимальное количество файлов: ${maxCount}`));
            return;
        }

        const input = createSingleFileInput();
        const button = document.createElement('button');
        button.textContent = `Выбрать файл ${currentFileIndex + 1}`;
        button.className = 'btn btn-primary btn-sm';
        button.style.cssText = `
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            background: #007bff;
            color: white;
            cursor: pointer;
            font-size: 14px;
        `;

        button.addEventListener('click', () => {
            input.click();
        });

        input.addEventListener('change', (event) => {
            const file = event.target.files[0];
            if (file) {
                selectedFiles.push(file);
                
                // Показываем выбранный файл
                const fileInfo = document.createElement('div');
                fileInfo.className = 'selected-file';
                fileInfo.style.cssText = `
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    padding: 8px;
                    background: #e9ecef;
                    border-radius: 4px;
                    margin-top: 5px;
                `;
                
                fileInfo.innerHTML = `
                    <span style="flex: 1; margin-right: 10px;">${file.name}</span>
                    <button class="btn btn-danger btn-sm" style="padding: 2px 8px; font-size: 12px;">
                        Удалить
                    </button>
                `;
                
                const removeBtn = fileInfo.querySelector('button');
                removeBtn.addEventListener('click', () => {
                    const index = selectedFiles.indexOf(file);
                    if (index > -1) {
                        selectedFiles.splice(index, 1);
                    }
                    fileInfo.remove();
                    currentFileIndex--;
                    
                    // Обновляем нумерацию кнопок
                    updateButtonNumbers();
                });
                
                container.appendChild(fileInfo);
                currentFileIndex++;
                
                // Обновляем нумерацию кнопок
                updateButtonNumbers();
                
                // Вызываем callback
                onFilesSelected([...selectedFiles]);
            }
        });

        container.appendChild(button);
        currentFileIndex++;
    };

    // Функция для обновления нумерации кнопок
    const updateButtonNumbers = () => {
        const buttons = container.querySelectorAll('button.btn-primary');
        buttons.forEach((button, index) => {
            button.textContent = `Выбрать файл ${index + 1}`;
        });
    };

    // Добавляем первую кнопку
    addFile();

    // Добавляем кнопку "Добавить еще" если нужно несколько файлов
    if (multiple && maxCount > 1) {
        const addMoreButton = document.createElement('button');
        addMoreButton.textContent = 'Добавить еще файл';
        addMoreButton.className = 'btn btn-secondary btn-sm';
        addMoreButton.style.cssText = `
            padding: 8px 16px;
            border: none;
            border-radius: 4px;
            background: #6c757d;
            color: white;
            cursor: pointer;
            font-size: 14px;
            margin-top: 10px;
        `;
        
        addMoreButton.addEventListener('click', addFile);
        container.appendChild(addMoreButton);
    }

    // Добавляем кнопку очистки
    const clearButton = document.createElement('button');
    clearButton.textContent = 'Очистить все';
    clearButton.className = 'btn btn-danger btn-sm';
    clearButton.style.cssText = `
        padding: 8px 16px;
        border: none;
        border-radius: 4px;
        background: #dc3545;
        color: white;
        cursor: pointer;
        font-size: 14px;
        margin-top: 10px;
    `;
    
    clearButton.addEventListener('click', () => {
        selectedFiles.length = 0;
        currentFileIndex = 0;
        
        // Удаляем все файлы из отображения
        const fileInfos = container.querySelectorAll('.selected-file');
        fileInfos.forEach(info => info.remove());
        
        // Удаляем все кнопки кроме первой
        const buttons = container.querySelectorAll('button.btn-primary');
        buttons.forEach((button, index) => {
            if (index > 0) button.remove();
        });
        
        // Обновляем нумерацию
        updateButtonNumbers();
        
        onFilesSelected([]);
    });
    
    container.appendChild(clearButton);

    return {
        container,
        getFiles: () => [...selectedFiles],
        clear: () => {
            selectedFiles.length = 0;
            currentFileIndex = 0;
        }
    };
}

/**
 * Альтернативное решение через множественные input'ы
 */
export function createMultipleFileInputs(options = {}) {
    const {
        multiple = true,
        accept = 'image/*, video/*',
        maxCount = 10,
        onFilesSelected = () => {},
        onError = () => {}
    } = options;

    const inputs = [];
    const selectedFiles = [];

    // Создаем несколько скрытых input'ов
    for (let i = 0; i < maxCount; i++) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.style.display = 'none';
        input.multiple = false;
        
        input.addEventListener('change', (event) => {
            const file = event.target.files[0];
            if (file) {
                selectedFiles[i] = file;
                onFilesSelected(selectedFiles.filter(f => f));
            }
        });
        
        inputs.push(input);
    }

    return {
        inputs,
        getFiles: () => selectedFiles.filter(f => f),
        triggerInput: (index) => {
            if (inputs[index]) {
                inputs[index].click();
            }
        },
        clear: () => {
            selectedFiles.length = 0;
            inputs.forEach(input => {
                input.value = '';
            });
        }
    };
}

/**
 * Решение через drag & drop для Android WebView
 */
export function createDragDropFileSelector(options = {}) {
    const {
        multiple = true,
        accept = 'image/*, video/*',
        maxCount = 10,
        onFilesSelected = () => {},
        onError = () => {}
    } = options;

    const container = document.createElement('div');
    container.className = 'drag-drop-file-selector';
    container.style.cssText = `
        border: 2px dashed #ccc;
        border-radius: 8px;
        padding: 40px 20px;
        text-align: center;
        background: #f9f9f9;
        transition: all 0.3s ease;
        cursor: pointer;
    `;

    container.innerHTML = `
        <div style="font-size: 16px; color: #666; margin-bottom: 10px;">
            <i class="fas fa-cloud-upload-alt" style="font-size: 24px; margin-bottom: 10px;"></i>
            <br>
            Перетащите файлы сюда или нажмите для выбора
        </div>
        <div style="font-size: 12px; color: #999;">
            Поддерживаемые форматы: изображения и видео
        </div>
    `;

    const selectedFiles = [];

    // Обработка клика
    container.addEventListener('click', () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = accept;
        input.multiple = multiple;
        
        input.addEventListener('change', (event) => {
            const files = Array.from(event.target.files);
            if (files.length > maxCount) {
                onError(new Error(`Максимальное количество файлов: ${maxCount}`));
                return;
            }
            
            selectedFiles.length = 0;
            selectedFiles.push(...files);
            onFilesSelected([...selectedFiles]);
            updateDisplay();
        });
        
        input.click();
    });

    // Обработка drag & drop
    container.addEventListener('dragover', (e) => {
        e.preventDefault();
        container.style.borderColor = '#007bff';
        container.style.background = '#e3f2fd';
    });

    container.addEventListener('dragleave', (e) => {
        e.preventDefault();
        container.style.borderColor = '#ccc';
        container.style.background = '#f9f9f9';
    });

    container.addEventListener('drop', (e) => {
        e.preventDefault();
        container.style.borderColor = '#ccc';
        container.style.background = '#f9f9f9';
        
        const files = Array.from(e.dataTransfer.files);
        if (files.length > maxCount) {
            onError(new Error(`Максимальное количество файлов: ${maxCount}`));
            return;
        }
        
        selectedFiles.length = 0;
        selectedFiles.push(...files);
        onFilesSelected([...selectedFiles]);
        updateDisplay();
    });

    const updateDisplay = () => {
        if (selectedFiles.length > 0) {
            container.innerHTML = `
                <div style="font-size: 14px; color: #28a745; margin-bottom: 10px;">
                    <i class="fas fa-check-circle" style="font-size: 20px;"></i>
                    <br>
                    Выбрано файлов: ${selectedFiles.length}
                </div>
                <div style="font-size: 12px; color: #666;">
                    ${selectedFiles.map(f => f.name).join(', ')}
                </div>
            `;
        } else {
            container.innerHTML = `
                <div style="font-size: 16px; color: #666; margin-bottom: 10px;">
                    <i class="fas fa-cloud-upload-alt" style="font-size: 24px; margin-bottom: 10px;"></i>
                    <br>
                    Перетащите файлы сюда или нажмите для выбора
                </div>
                <div style="font-size: 12px; color: #999;">
                    Поддерживаемые форматы: изображения и видео
                </div>
            `;
        }
    };

    return {
        container,
        getFiles: () => [...selectedFiles],
        clear: () => {
            selectedFiles.length = 0;
            updateDisplay();
        }
    };
} 